import axios from 'axios';

import config from '../../config/config';

import defaultHeaders from './base/default-headers';
import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

/**
 * Send a request whose body or response may carry an OCPP AuthorizationKey.
 * Unlike the shared base helpers this never logs the error, because an
 * AxiosError includes the request body and response data.
 * @param {'get' | 'post' | 'patch'} method - The HTTP method
 * @param {string} url - The request URL
 * @param {unknown} data - The request body (ignored for GET)
 * @param {boolean} isFileUpload - Whether `data` is a multipart FormData body
 * @returns {Promise<any>} The raw axios response
 */
const sensitiveRequest = (
  method: 'get' | 'post' | 'patch',
  url: string,
  data?: unknown,
  isFileUpload = false,
): Promise<any> => {
  const headers = {
    ...defaultHeaders(),
    ...(isFileUpload ? { 'Content-Type': 'multipart/form-data' } : {}),
  } as Record<string, string>;
  if (method === 'get') {
    return axios.get(url, { headers });
  }
  return axios[method](url, data, { headers });
};

export default {
  /**
   * Fetch all device brands
   * @param {any} query - The query
   */
  fetchBrands: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/brands`, {
      ...query,
    });
  },

  /**
   * Export brands from device hub
   * @param {any} query - The query parameters
   * @returns {Promise<any>} A promise resolving with the export response
   */
  exportBrands: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/brands/export`, {
      ...query,
    });
  },
  /**
   * Fetch global brands list
   * @param {any} query - The query parameters
   */
  fetchGlobalBrands: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/global-parameters/brands/list`,
      {
        ...query,
      },
    );
  },
  /**
   * Fetch all device
   * @param {any} query - The query
   */
  fetchAllDevices: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices`, {
      ...query,
    });
  },

  /**
   * Export devices from device hub
   * @param {any} query - The query parameters
   * @returns {Promise<any>} A promise resolving with the export response
   */
  exportDevices: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/export`, {
      ...query,
    });
  },

  /**
   * Fetch device details
   */
  fetchDeviceDetails: (): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/device-details`);
  },

  /**
   * Create a new device brand
   * @param {any} data - The data
   */
  createBrand: (data: any): Promise<any> => {
    return postRequest(`${config.devices.ChargersApiUrl}api/admin/v1/brands`, data);
  },

  /**
   * Delete an existing device brand
   * @param {any} id - The id
   * @returns {Promise<any>} Promise resolving with response
   */
  deleteBrand: (id: string): Promise<any> => {
    return deleteRequest(`${config.devices.ChargersApiUrl}api/admin/v1/brands/${id}`);
  },

  /**
   * Create a new device. The body may carry an entered AuthorizationKey and the
   * response may carry a generated one, so the request is never logged.
   * @param {any} data - The data
   * @returns {Promise<any>} A promise resolving with the created device (and `authorizationKey` when generated)
   */
  addDevice: (data: any): Promise<any> => {
    return sensitiveRequest('post', `${config.devices.ChargersApiUrl}api/admin/v1/devices/`, data);
  },
  /**
   * Fetch device by ID
   * @param {any} id - The id
   */
  fetchDeviceById: (id: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}`);
  },
  /**
   * Update an existing device. The body may carry a `security.authorizationKey` and the
   * response may carry a generated/replaced one, so this is never logged.
   * @param {any} id - The id
   * @param {any} data - The data
   * @param {any} isFileUpload - The isFileUpload
   * @returns {Promise<any>} Promise resolving with response
   */
  updateDevice: (id: string, data: any, isFileUpload = false): Promise<any> => {
    return sensitiveRequest(
      'patch',
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}`,
      data,
      isFileUpload,
    );
  },
  /**
   * Update an attached charger's protocols and/or security settings. Unlike the generic
   * PATCH /devices/:id, this works for chargers that already have an owner attached, and
   * only accepts firmwareProtocolTypes / diagnosticProtocolTypes / security. The body may
   * carry a credential and the response may carry a generated one, so this is never logged.
   * @param {string} id - The device ID
   * @param {Record<string, unknown>} data - The partial update body (send only changed keys)
   * @returns {Promise<any>} A promise resolving with `{ device, authorizationKey }`
   */
  updateAttachedDeviceSettings: (id: string, data: Record<string, unknown>): Promise<any> => {
    return sensitiveRequest(
      'patch',
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}/attached-settings`,
      data,
    );
  },

  /**
   * Update an existing device brand via PATCH
   * @param {string} id - The brand ID
   * @param {any} data - The fields to update
   * @returns {Promise<any>} Promise resolving with updated brand
   */
  updateBrand: (id: string, data: any): Promise<any> => {
    return patchRequest(`${config.devices.ChargersApiUrl}api/admin/v1/brands/${id}`, data);
  },

  /**
   * Update an existing device model via PATCH
   * @param {string} id - The model ID
   * @param {any} data - The fields to update
   * @param {boolean} isFileUpload - Whether the request contains file upload
   * @returns {Promise<any>} Promise resolving with updated model
   */
  updateModel: (id: string, data: any, isFileUpload = false): Promise<any> => {
    return patchRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/models/${id}`,
      data,
      isFileUpload,
    );
  },
  /**
   * Delete an existing device
   * @param {any} id - The id
   */
  deleteDevice: (id: string): Promise<any> => {
    return deleteRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}/`);
  },

  /**
   * Blacklist a device by serial number
   * @param {object} data - The payload containing deviceSerialNumber and reason
   * @param {string} data.deviceSerialNumber - The device serial number
   * @param {string} [data.reason] - The reason for blacklisting
   * @returns {Promise<any>} Promise resolving with response
   */
  blacklistDevice: (data: { deviceSerialNumber: string; reason?: string }): Promise<any> => {
    return postRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/blacklist`, data);
  },

  /**
   * Whitelist / Unblock a device by serial number
   * @param {object} data - The payload containing deviceSerialNumber and reason
   * @param {string} data.deviceSerialNumber - The device serial number
   * @param {string} [data.reason] - The reason for whitelisting
   * @returns {Promise<any>} Promise resolving with response
   */
  whitelistDevice: (data: { deviceSerialNumber: string; reason?: string }): Promise<any> => {
    return postRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/whitelist`, data);
  },

  /**
   * Fetch device models by brand ID or general
   * @param {any} query - The query
   */
  fetchModels: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/models`, {
      ...query,
    });
  },

  /**
   * Export models from device hub
   * @param {QueryParameters} query - The query parameters
   * @returns {Promise<any>} A promise resolving with the export response
   */
  exportModels: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/models/export`, {
      ...query,
    });
  },
  /**
   * Fetch global models list
   * @param {any} query - The query parameters
   */
  fetchGlobalModels: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/global-parameters/models/list`,
      {
        ...query,
      },
    );
  },

  /**
   * Create a new device model
   * @param {any} data - The data
   * @param {boolean} isFileUpload - Whether the request contains file upload
   * @returns {Promise<any>} Promise resolving with response
   */
  createModel: (data: any, isFileUpload = false): Promise<any> => {
    return postRequest(`${config.devices.ChargersApiUrl}api/admin/v1/models`, data, isFileUpload);
  },

  /**
   * Delete an existing device model
   * @param {any} id - The id
   * @returns {Promise<any>} Promise resolving with response
   */
  deleteModel: (id: string): Promise<any> => {
    return deleteRequest(`${config.devices.ChargersApiUrl}api/admin/v1/models/${id}`);
  },

  /**
   * Fetch a single device model by ID
   * @param {string} id - The model ID
   * @returns {Promise<any>} Promise resolving with the model data
   */
  fetchModelById: (id: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/models/${id}`);
  },

  /**
   * Upload a new firmware artifact (binary file + metadata)
   * @param {any} data - The data (FormData when uploading a binary)
   * @param {boolean} isFileUpload - Whether the request contains file upload
   * @returns {Promise<any>} Promise resolving with response
   */
  uploadFirmwareArtifact: (data: any, isFileUpload = true): Promise<any> => {
    return postRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/firmware/artifacts`,
      data,
      isFileUpload,
    );
  },

  /**
   * Fetch all firmware artifacts
   * @param {QueryParameters} query - The query parameters
   * @returns {Promise<any>} Promise resolving with response
   */
  fetchFirmwareArtifacts: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/firmware/artifacts`, {
      ...query,
    });
  },

  /**
   * Fetch a single firmware artifact by id
   * @param {string} id - The firmware artifact id
   * @returns {Promise<any>} Promise resolving with response
   */
  fetchFirmwareArtifactById: (id: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/firmware/artifacts/${id}`);
  },

  /**
   * Download a firmware artifact's binary as a Blob. Built as a standalone
   * axios call (not via getRequest) because the shared base helpers don't
   * support `responseType: 'blob'`; reuses defaultHeaders() directly so
   * auth/domain headers stay identical to every other request.
   * @param {string} id - The firmware artifact id
   * @returns {Promise<Blob>} Promise resolving with the firmware binary blob
   */
  downloadFirmwareArtifact: async (id: string): Promise<Blob> => {
    const authToken = defaultHeaders();
    const headers = {
      ...(authToken && authToken),
    };
    const response = await axios.get(
      `${config.devices.ChargersApiUrl}api/admin/v1/firmware/artifacts/${id}/download`,
      { headers, responseType: 'blob' },
    );
    return response.data;
  },

  /**
   * Delete an existing firmware artifact
   * @param {string} id - The firmware artifact id
   * @returns {Promise<any>} Promise resolving with response
   */
  deleteFirmwareArtifact: (id: string): Promise<any> => {
    return deleteRequest(`${config.devices.ChargersApiUrl}api/admin/v1/firmware/artifacts/${id}`);
  },

  /**
   * Create a new Connector Rating
   * @param {any} data - The data
   */
  createConnecterRating: (data: any): Promise<any> => {
    return postRequest(`${config.devices.ChargersApiUrl}api/admin/v1/connector-ratings`, data);
  },

  /**
   * Delete an existing connector rating
   * @param {any} id - The id
   * @returns {Promise<any>} Promise resolving with response
   */
  deleteConnectorRating: (id: string): Promise<any> => {
    return deleteRequest(`${config.devices.ChargersApiUrl}api/admin/v1/connector-ratings/${id}`);
  },

  /**
   * Update an existing connector rating via PATCH
   * @param {string} id - The connector rating ID
   * @param {any} data - The fields to update
   * @returns {Promise<any>} Promise resolving with updated connector rating
   */
  updateConnectorRating: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/connector-ratings/${id}`,
      data,
    );
  },

  /**
   * Fetch device models by brand ID or general
   * @param {any} query - The query
   */
  connecterRateList: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/connector-ratings`, {
      ...query,
    });
  },
  /**
   * Export connector ratings data
   * @param {QueryParameters} query - The query parameters
   * @returns {Promise<any>} A promise that resolves with the export data
   */
  exportConnectorRatings: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/connector-ratings/export`, {
      ...query,
    });
  },
  /**
   * Fetch global connector ratings list
   * @param {any} query - The query parameters
   */
  fetchGlobalConnectorRatings: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/global-parameters/connector-ratings/list`,
      {
        ...query,
      },
    );
  },
  /**
   * Fetch device filters
   * @param {any} query query
   * @returns {any} promise
   */
  deviceFilter: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/filters`, {
      ...query,
    });
  },
  /**
   * Get brand filters
   * @param {any} query query
   * @returns {any} promise
   */
  brandFilter: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/brands/filters`, {
      ...query,
    });
  },
  /**
   * Get model filters
   * @param {any} query query
   * @returns {any} promise
   */
  modelFilter: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/models/filters`, {
      ...query,
    });
  },
  /**
   * Get connector rating filters
   * @param {any} query query
   * @returns {any} promise
   */
  connectorRatingFilter: (query: QueryParameters = {}): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/connector-ratings/filters`, {
      ...query,
    });
  },
  /**
   * Get charger connector status
   * @returns {any} promise
   */
  connectorStatus: (): Promise<any> => {
    return getRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/connector-status-count`,
    );
  },
  /**
   * Fetches connector details for a specific EV charging connector.
   * @param {string} id - Unique identifier of the connector.
   * @returns {any} A promise that resolves with the connector details.
   */
  fetchConnectorData: (id: string): Promise<any> => {
    return getRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/connector-details/${id}`,
    );
  },
  /**
   * Fetches advanced device details by ID
   * @param {string} id - The ID of the device
   * @returns {any} A promise that resolves with the advanced device details
   */
  fetchAdvanceDeviceDetails: (id: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}`);
  },
  /**
   * Fetch tenant websocket URL by tenant name
   * @param {any} tenantName - The tenantName
   */
  fetchTenantWebsocket: (tenantName: string): Promise<any> => {
    return getRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/tenant-websocket/${tenantName}`,
    );
  },
  /**
   * Fetch available firmware artifacts for a specific charging point
   * @param {string} chargingPointSn - The charging point serial number
   * @returns {Promise<any>} A promise resolving with the firmware artifacts
   */
  fetchAvailableFirmwareArtifacts: (chargingPointSn: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/firmware/artifacts/available`, { chargingPointSn });
  },
  /**
   * Fetch the transfer protocols a charging point currently supports for a given feature
   * @param {string} chargingPointSn - The charging point serial number
   * @param {'diagnostics' | 'firmware'} type - The feature to query supported protocols for
   * @returns {Promise<any>} A promise resolving with the supported protocols
   */
  fetchSupportedProtocols: (
    chargingPointSn: string,
    type: 'diagnostics' | 'firmware',
  ): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/devices/supported-protocols`, {
      chargingPointSn,
      type,
    });
  },
  /**
   * Update a charger's OCPP security settings (expected profile, reason, credential).
   * Send only the fields that changed.
   * @param {string} id - The device ID
   * @param {object} data - The partial security update body
   * @returns {Promise<any>} A promise resolving with `{ security, authorizationKey }`
   */
  updateDeviceSecurity: (id: string, data: Record<string, unknown>): Promise<any> => {
    return sensitiveRequest(
      'patch',
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}/security`,
      data,
    );
  },
  /**
   * Turn "disconnect on low security profile" on or off for a charger (Profile 1 or 2 only). Turning
   * it on disconnects a charger currently connected below its expected profile and refuses every
   * future below-profile connection; turning it off requires an audited reason.
   * @param {string} id - The device ID
   * @param {object} data - The request body
   * @param {boolean} data.enforce - True to disconnect/refuse below-profile connections, false to allow them
   * @param {string} data.reason - Audited reason; required when `enforce` is false
   * @returns {Promise<any>} A promise resolving with `{ security, disconnected }`
   */
  updateDeviceSecurityEnforcement: (
    id: string,
    data: { enforce: boolean; reason?: string },
  ): Promise<any> => {
    return sensitiveRequest(
      'patch',
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}/security/enforcement`,
      data,
    );
  },
  /**
   * Reveal the stored AuthorizationKey of a charger. Every call is audited server-side,
   * so call it only on an explicit user click and never cache the result.
   * @param {string} id - The device ID
   * @returns {Promise<any>} A promise resolving with `{ authorizationKey, credentialSource, factoryCredential }`
   */
  fetchDeviceSecurityCredential: (id: string): Promise<any> => {
    return sensitiveRequest(
      'get',
      `${config.devices.ChargersApiUrl}api/admin/v1/devices/${id}/security/credential`,
    );
  },
  /**
   * Fetch the currently pending (non-terminal) firmware job for a charging point, if any
   * @param {string} chargingPointSn - The charging point serial number
   * @returns {Promise<any>} A promise resolving with the pending firmware job status
   */
  fetchPendingFirmwareJob: (chargingPointSn: string): Promise<any> => {
    return getRequest(`${config.devices.ChargersApiUrl}api/admin/v1/firmware/jobs/pending`, {
      chargingPointSn,
    });
  },
  /**
   * Cancel a firmware job. This only marks the job record as cancelled —
   * it cannot stop a charger that is already downloading/installing, since
   * OCPP 1.6J has no cancellation message for UpdateFirmware.
   * @param {string} jobId - The firmware job's _id
   * @param {string} [reason] - Optional reason for the cancellation
   * @returns {Promise<any>} A promise resolving with the updated (cancelled) firmware job
   */
  cancelFirmwareJob: (jobId: string, reason?: string): Promise<any> => {
    return postRequest(
      `${config.devices.ChargersApiUrl}api/admin/v1/firmware/jobs/${jobId}/cancel`,
      reason ? { reason } : {},
    );
  },
};
